/* eslint eslint-comments/no-use: off */
// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {getIssueSearchQueries} from '@github-ui/item-picker-base/shared'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'

import type {IssuePickerItem} from '../components/IssuePicker'
import type {useIssueFilteringQuery, useIssueFilteringQuery$data} from './__generated__/useIssueFilteringQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const useIssueFilteringQueryGraphQLQuery = graphql`
  query useIssueFilteringQuery(
    $commenters: String!
    $mentions: String!
    $assignee: String!
    $author: String!
    $other: String!
    $first: Int = 10
    $resource: URI!
    $queryIsUrl: Boolean!
    $withParent: Boolean!
    $requireTargetTriage: Boolean = false
  ) {
    commenters: search(type: ISSUE, first: $first, query: $commenters) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    mentions: search(type: ISSUE, first: $first, query: $mentions) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    assignee: search(type: ISSUE, first: $first, query: $assignee) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    author: search(type: ISSUE, first: $first, query: $author) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    other: search(type: ISSUE, first: $first, query: $other) {
      nodes {
        ... on Issue {
          id
          title
          state
          stateReason
          repository {
            id
            nameWithOwner
          }
          databaseId
          number
          url
          viewerCanUpdateMetadata @include(if: $requireTargetTriage)
          __typename
          parent @include(if: $withParent) {
            id
          }
        }
      }
    }
    # Do not return resource unless it is an Issue
    resource: resource(url: $resource) @include(if: $queryIsUrl) {
      ... on Issue {
        id
        title
        state
        stateReason
        repository {
          id
          nameWithOwner
        }
        databaseId
        number
        url
        viewerCanUpdateMetadata @include(if: $requireTargetTriage)
        __typename
        parent @include(if: $withParent) {
          id
        }
      }
    }
  }
`

// Handles debouncing queries to fetch issues based on the current filter
export const useIssueFiltering = (
  filter: string,
  owner: string = '',
  repositoryNameWithOwner?: string,
  hiddenIssueIds?: string[],
  withParent: boolean = false,
  requireTargetTriage: boolean = false,
): {
  isLoading: boolean
  isError: boolean
  items: Map<string, IssuePickerItem>
} => {
  const environment = useRelayEnvironment()

  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<useIssueFilteringQuery$data | null>(null)

  const activeSubscriptionRef = useRef<{unsubscribe: () => void} | null>(null)

  const fetchSearchData = useCallback(
    (nextFilter: string) => {
      setIsLoading(true)

      // Cancel any existing subscription to prevent race conditions
      activeSubscriptionRef.current?.unsubscribe()

      const subscription = clientSideRelayFetchQueryRetained<useIssueFilteringQuery>({
        environment,
        query: useIssueFilteringQueryGraphQLQuery,
        variables: {
          ...getIssueSearchQueries(owner, nextFilter, repositoryNameWithOwner),
          withParent,
          requireTargetTriage,
        },
      }).subscribe({
        next: (innerData: useIssueFilteringQuery$data) => {
          setData(innerData ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      })

      activeSubscriptionRef.current = subscription
    },
    [environment, owner, repositoryNameWithOwner, withParent, requireTargetTriage],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  useEffect(() => {
    debounceFetchSearchData(filter)
  }, [debounceFetchSearchData, filter])

  useEffect(() => {
    return () => {
      // Always clean up subscription on unmount to prevent memory leaks
      activeSubscriptionRef.current?.unsubscribe()
    }
  }, [])

  const items: Map<string, IssuePickerItem> = useMemo(() => {
    if (!data) {
      return new Map()
    } else {
      // When resource is returned, we only want to show that issue as the user's intent is to
      // select a specific issue otherwise, we show all the issues from the search queries
      if (data.resource?.__typename === 'Issue') {
        // Best-effort hide of a directly-resolved issue the viewer can't triage (viewerCanUpdateMetadata
        // reflects base repo role); the mutation-level FORBIDDEN handling is the authoritative backstop.
        if (requireTargetTriage && data.resource.viewerCanUpdateMetadata !== true) {
          return new Map()
        }
        return new Map([[data.resource?.id, data.resource]])
      }

      // Order the unique issues from each of the queries. Changing the order of this spread
      // will change the relative priority of each individual search query.
      const searchResultNodes = [
        ...(data.commenters?.nodes || []),
        ...(data.mentions?.nodes || []),
        ...(data.assignee?.nodes || []),
        ...(data.author?.nodes || []),
        ...(data.other?.nodes || []),
      ]

      // viewerCanUpdateMetadata is only @include'd when requireTargetTriage is true, so guard the read.
      const issueNodes = searchResultNodes.filter(
        (node): node is IssuePickerItem =>
          node?.__typename === 'Issue' && (!requireTargetTriage || node.viewerCanUpdateMetadata === true),
      )

      const issuesMap = new Map<string, IssuePickerItem>()
      for (const issue of issueNodes) {
        if (hiddenIssueIds?.some(id => id === issue.id)) {
          continue
        }

        issuesMap.set(issue.id, issue)
      }

      return issuesMap
    }
  }, [hiddenIssueIds, data, requireTargetTriage])

  return {isLoading, isError, items}
}
